/* Copyright (c) 2026 Hyegro Pty Ltd. All rights reserved. */

/** Payment plans — GET /re/payment-plans (filter project_id; null project = global). */

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Icon } from '@mui/material';
import MDButton from 'components/MDButton';
import { usePermission } from 'auth/usePermission';
import useDebounce from 'hooks/useDebounce';
import RePage, { FilterBar, LoadError, RePanel, ReSearch } from './components/RePage';
import ReTable from './components/ReTable';
import { RePicker } from './components/Pickers';
import { triggerText } from './components/SchedulePreview';
import { formatPct, useReList } from './api';

const summarise = (milestones) => {
  const ms = [...(milestones || [])].sort((a, b) => a.sequence - b.sequence);
  if (!ms.length) return '—';
  const shown = ms.slice(0, 4).map((m) => `${formatPct(m.percentage, 0)} ${triggerText(m).toLowerCase()}`);
  return ms.length > 4 ? `${shown.join(', ')} … (+${ms.length - 4})` : shown.join(', ');
};

export default function PaymentPlans() {
  const navigate = useNavigate();
  const { canCreate } = usePermission('re_payment_plans');
  const [projectId, setProjectId] = useState(null);
  const [search, setSearch] = useState('');
  const debounced = useDebounce(search, 350);
  const list = useReList('/re/payment-plans', { project_id: projectId, search: debounced });

  return (
    <RePage
      title="Payment plans"
      subtitle="Milestone templates used to build booking installment schedules"
      actions={canCreate && (
        <MDButton variant="gradient" color="info" size="small" onClick={() => navigate('/real-estate/payment-plans/new')}>
          <Icon>add</Icon>&nbsp;New plan
        </MDButton>
      )}
    >
      <FilterBar>
        <ReSearch label="Search" value={search} onChange={setSearch} />
        <RePicker path="/re/projects" label="Project" value={projectId} onChange={setProjectId} />
      </FilterBar>
      <LoadError error={list.error} onRetry={list.reload} />
      <RePanel p={0}>
        <ReTable
          loading={list.loading}
          rows={list.items}
          emptyMessage="No payment plans yet."
          onRowClick={(r) => navigate(`/real-estate/payment-plans/${r.id}`)}
          total={list.total}
          page={list.page}
          pageSize={list.pageSize}
          onPageChange={list.setPage}
          onPageSizeChange={list.setPageSize}
          columns={[
            { key: 'name', label: 'Plan', render: (r) => <strong>{r.name}</strong> },
            { key: 'project_name', label: 'Project', render: (r) => r.project_name || 'Global' },
            { key: 'milestones', label: 'Milestones', render: (r) => summarise(r.milestones) },
            { key: 'is_post_handover', label: 'Post-handover', render: (r) => (r.is_post_handover ? 'Yes' : 'No') },
            { key: 'is_active', label: 'Active', render: (r) => (r.is_active ? 'Yes' : 'No') },
          ]}
        />
      </RePanel>
    </RePage>
  );
}
